import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, MessageCircleX, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  pendiente:   'bg-yellow-100 text-yellow-800',
  encamino:    'bg-blue-100 text-blue-800',
  postergado:  'bg-orange-100 text-orange-800',
  recogido:    'bg-purple-100 text-purple-800',
};

export function NoResponseModal({ selectedDate, onClose, onCountLoaded }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading]       = useState(true);
  

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/deliveries/no-response`, {
          params: { date: selectedDate }
        });
        setDeliveries(res.data);
        onCountLoaded?.(res.data.length);
      } catch (err) {
        console.error('Error fetching no-response deliveries:', err);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [selectedDate]);

  function buildWaLink(phone) {
    if (!phone) return '#';
    const digits = phone.replace(/\D/g, '');
    const normalized = digits.startsWith('51') ? digits : '51' + digits;
    return `https://wa.me/${normalized}`;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100">
              <MessageCircleX className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800 font-montserrat">
                Sin respuesta del cliente
              </h2>
              <p className="text-xs text-slate-500">
                {selectedDate} · {deliveries.length} entrega
                {deliveries.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
              Cargando...
            </div>
          ) : deliveries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2">
              <MessageCircleX className="w-10 h-10 text-slate-300" />
              <p className="text-slate-400 text-sm">
                Todos los clientes han respondido
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  {[
                    "Estado",
                    "ID",
                    "Nombre",
                    "Número",
                    "Tienda",
                    "ID Tienda",
                    "Producto",
                    "Acción",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deliveries.map((dp) => (
                  <tr
                    key={dp.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[dp.status] ?? "bg-slate-100 text-slate-600"}`}
                      >
                        {dp.status}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                      #{dp.id}
                    </td>

                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                      {dp.recipient_name}
                    </td>

                    <td className="px-4 py-3 text-slate-600 font-mono text-xs whitespace-nowrap">
                      {dp.recipient_phone}
                    </td>

                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {dp.user_name}
                    </td>

                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                      #{dp.user_id}
                    </td>

                    <td
                      className="px-4 py-3 text-slate-600 max-w-[160px] truncate"
                      title={dp.product}
                    >
                      {dp.product ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={buildWaLink(dp.user_phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium transition-colors whitespace-nowrap"
                        title={`Contactar a ${dp.user_name} por WhatsApp`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Tienda
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}